import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Layout from '../components/Layout';

// ─── constants ───────────────────────────────────────────────────────────────

const TYPE_META = {
  video:     { label: 'Video',     color: '#003479', bg: 'rgba(0,52,121,0.10)',   icon: 'M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664zM21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  article:   { label: 'Article',   color: '#20C997', bg: 'rgba(32,201,151,0.10)', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  job_alert: { label: 'Job Alert', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  tip:       { label: 'Tip',       color: '#8b5cf6', bg: 'rgba(139,92,246,0.10)', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
};

const TYPES = Object.keys(TYPE_META);

// ─── helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const TypeBadge = ({ type }) => {
  const meta = TYPE_META[type] || TYPE_META.tip;
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold flex-shrink-0"
      style={{ color: meta.color, background: meta.bg }}
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={meta.icon} />
      </svg>
      {meta.label}
    </span>
  );
};

// ─── Feed Card ────────────────────────────────────────────────────────────────

const FeedCard = ({ item, onRead, onOpen }) => {
  const meta = TYPE_META[item.type] || TYPE_META.tip;
  const isUnread = !item.is_read;

  const handleClick = () => {
    if (isUnread) onRead(item.id);
    if (item.link) onOpen(item.link);
  };

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm transition-all hover:shadow-md ${
        isUnread ? 'border-l-4' : 'border-gray-200'
      }`}
      style={isUnread ? { borderLeftColor: meta.color, borderTopColor: '#e5e7eb', borderRightColor: '#e5e7eb', borderBottomColor: '#e5e7eb' } : {}}
    >
      <div className="p-5">
        {/* Top row */}
        <div className="flex items-start gap-3">
          {/* Colored icon */}
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{ background: meta.bg }}
          >
            <svg className="w-5 h-5" style={{ color: meta.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={meta.icon} />
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            {/* Badge + unread dot */}
            <div className="flex items-center gap-2 mb-1.5">
              <TypeBadge type={item.type} />
              {isUnread && (
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#20C997' }} />
              )}
              {item.batch_name && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-lg flex-shrink-0">
                  {item.batch_name}
                </span>
              )}
            </div>

            {/* Title */}
            <h3 className={`font-semibold text-base leading-snug ${isUnread ? 'text-gray-900' : 'text-gray-700'}`}>
              {item.title}
            </h3>

            {/* Content */}
            {item.content && (
              <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{item.content}</p>
            )}

            {/* CTA */}
            {item.link && (
              <button
                onClick={handleClick}
                className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 text-xs font-semibold rounded-lg text-white transition-all hover:opacity-90"
                style={{ background: meta.color }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                {item.type === 'video' ? 'Watch Video' :
                 item.type === 'article' ? 'Read Article' :
                 item.type === 'job_alert' ? 'View Job' : 'Open Link'}
              </button>
            )}

            {/* Footer */}
            <div className="flex items-center gap-3 mt-3">
              <span className="text-xs text-gray-400">{formatDate(item.created_at)}</span>
              {item.creator_name && (
                <span className="text-xs text-gray-400">
                  by <span className="font-medium text-gray-500">{item.creator_name}</span>
                </span>
              )}
              {isUnread && (
                <button
                  onClick={() => onRead(item.id)}
                  className="text-xs text-gray-400 hover:text-gray-600 ml-auto underline underline-offset-2"
                >
                  Mark as read
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const StudentFeed = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get('/api/feed');
      setItems(res.data || []);
    } catch {
      setError('Failed to load your feed.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleRead = async (itemId) => {
    try {
      await api.post(`/api/feed/${itemId}/read`);
      setItems((prev) => prev.map((it) => it.id === itemId ? { ...it, is_read: true } : it));
    } catch {
      // silent
    }
  };

  const handleOpen = (link) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const unreadCount = items.filter((it) => !it.is_read).length;

  const filtered = items.filter((item) => {
    if (filterType && item.type !== filterType) return false;
    if (showUnreadOnly && item.is_read) return false;
    return true;
  });

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <div className="skeleton h-8 w-48" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
              <div className="flex items-center gap-3">
                <div className="skeleton w-10 h-10 rounded-xl flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-5 w-3/4" />
                  <div className="skeleton h-4 w-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium">
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(32,201,151,0.12)' }}>
            <svg className="w-5 h-5" style={{ color: '#20C997' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Learning Feed</h1>
            <p className="text-sm text-gray-500">
              {items.length} post{items.length !== 1 ? 's' : ''}
              {unreadCount > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: '#20C997' }}>
                  {unreadCount} new
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Type pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => setFilterType('')}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                !filterType ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={!filterType ? { background: 'linear-gradient(135deg,#20C997,#003479)' } : {}}
            >
              All
            </button>
            {TYPES.map((t) => {
              const meta = TYPE_META[t];
              const active = filterType === t;
              return (
                <button
                  key={t}
                  onClick={() => setFilterType(active ? '' : t)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                    active ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  style={active ? { background: meta.color } : { background: meta.bg, color: meta.color }}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>

          {/* Unread toggle */}
          <button
            onClick={() => setShowUnreadOnly(!showUnreadOnly)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ml-auto ${
              showUnreadOnly
                ? 'border-[#20C997] text-[#20C997] bg-[rgba(32,201,151,0.08)]'
                : 'border-gray-300 text-gray-600 hover:border-gray-400'
            }`}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: showUnreadOnly ? '#20C997' : '#d1d5db' }} />
            Unread only
          </button>
        </div>

        {/* Feed */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">
                {showUnreadOnly ? 'All caught up!' : filterType ? 'No posts of this type' : 'No posts yet'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {showUnreadOnly
                  ? 'You have read all available posts'
                  : 'Your instructor will share resources here soon'}
              </p>
              {(showUnreadOnly || filterType) && (
                <button
                  onClick={() => { setShowUnreadOnly(false); setFilterType(''); }}
                  className="mt-4 text-xs font-medium underline"
                  style={{ color: '#20C997' }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((item) => (
              <FeedCard
                key={item.id}
                item={item}
                onRead={handleRead}
                onOpen={handleOpen}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudentFeed;
